import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintIcon from '@mui/icons-material/Print';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { useDocumentsScreen } from '../hooks/useDocumentsScreen';
import DocumentListDialogs from './documentListDialogs';
import {
  DELETE_DISABLED_REASON,
  SIGN_DISABLED_REASON,
  hasSignedVersions,
  signatureStatus,
} from './documentSignatureStatus';

export default function DocumentListWeb() {
  const { patientId } = useParams();
  const screen = useDocumentsScreen(patientId);
  const { documents, isBusy, messages, editor, deletion, printDocument, signature } = screen;

  return (
    <>
      <Loading isToHidden={isBusy === false} />
      {messages.showSuccessMessage && (
        <SuccessMessage
          message={messages.successMessage}
          show={messages.showSuccessMessage}
          setShow={messages.setShowSuccessMessage}
        />
      )}
      {messages.showErrorMessage && (
        <ErrorMessage
          message={messages.errorMessage}
          show={messages.showErrorMessage}
          setShow={messages.setShowErrorMessage}
        />
      )}

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Documentos
          </Typography>
          <Button variant="outlined" onClick={editor.openNewDocument}>
            Novo Documento
          </Button>
        </Box>

        {documents.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhum documento cadastrado ainda.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Título</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Criado por</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Criado em</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Atualizado em</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Assinatura digital</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => {
                  const status = signatureStatus(document);
                  const versions = document.signedVersions ?? [];
                  const canDelete = !hasSignedVersions(document);
                  return (
                    <React.Fragment key={document.id}>
                      <TableRow
                        hover
                        sx={
                          versions.length > 0 ? { '& > td': { borderBottom: 'none' } } : undefined
                        }
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{document.title || '-'}</TableCell>
                        <TableCell>{document.createdBy || '-'}</TableCell>
                        <TableCell>{document.createdAt || '-'}</TableCell>
                        <TableCell>{document.updatedAt || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={status.color}
                            icon={
                              document.currentVersionSigned ? (
                                <VerifiedUserOutlinedIcon />
                              ) : undefined
                            }
                            label={status.label}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="Editar">
                            <IconButton
                              color="primary"
                              aria-label="editar"
                              onClick={() => editor.openDocument(document.id)}
                            >
                              <EditOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              document.currentVersionSigned
                                ? SIGN_DISABLED_REASON
                                : 'Assinar digitalmente'
                            }
                          >
                            <span>
                              <IconButton
                                color="primary"
                                aria-label="assinar digitalmente"
                                disabled={document.currentVersionSigned}
                                onClick={() => signature.openSignModal(document)}
                              >
                                <DrawOutlinedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Imprimir">
                            <IconButton
                              color="primary"
                              aria-label="imprimir"
                              onClick={() => printDocument(document.id)}
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={canDelete ? 'Excluir' : DELETE_DISABLED_REASON}>
                            <span>
                              <IconButton
                                color="error"
                                aria-label="excluir"
                                disabled={!canDelete}
                                onClick={() => deletion.handleDeleteClick(document)}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {versions.map((version, index) => (
                        <TableRow
                          key={`${document.id}-v${version.id}`}
                          sx={
                            index < versions.length - 1
                              ? { '& > td': { borderBottom: 'none' } }
                              : undefined
                          }
                        >
                          <TableCell colSpan={5} sx={{ py: 0.5, pl: 5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SubdirectoryArrowRightIcon fontSize="small" color="disabled" />
                              <VerifiedUserOutlinedIcon fontSize="small" color="success" />
                              <Typography variant="body2">
                                <strong>Versão {version.version}</strong> assinada em{' '}
                                {version.signedAt} por {version.signerName}
                                {version.title !== document.title && ` · "${version.title}"`}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}>
                            <Tooltip title={`Abrir PDF assinado (versão ${version.version})`}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`abrir PDF assinado da versão ${version.version}`}
                                onClick={() => signature.openSignedVersion(document, version)}
                              >
                                <PictureAsPdfOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <DocumentListDialogs patientId={patientId} screen={screen} />
    </>
  );
}
